import { useEffect, useState } from 'react'
import { api, type GeteilteNotiz } from '../api'
import { link } from '../navigation'
import { dauer, tagesDatum, tagesTitel, uhrzeit } from '../tagebuch'

const eigeneZeitzone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Von Freunden geteilte Notizen (#geteilt) bzw. eine einzelne (#geteilt/<id>, z. B. als Quelle einer Antwort).
 * Zu sehen ist nur, was der Freund gesagt hat, nicht seine Personen, Tags oder Termine.
 */
export function Geteilt({ id, onGesehen }: { id: number | null; onGesehen: () => void }) {
  const [notizen, setNotizen] = useState<GeteilteNotiz[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    const laden = id ? api.geteilt(id).then((n) => [n]) : api.geteilte()
    laden
      .then(async (n) => {
        setNotizen(n)
        if (n.some((x) => !x.gesehen)) {
          await api.geteilteGesehen()
          onGesehen()
        }
      })
      .catch((e) => setFehler((e as Error).message))
  }, [id])

  const ersetzen = (n: GeteilteNotiz) => setNotizen((alt) => alt?.map((x) => (x.id === n.id ? n : x)) ?? null)
  const entfernen = (nid: number) => setNotizen((alt) => alt?.filter((x) => x.id !== nid) ?? null)

  return (
    <div className="tagebuch geteilt">
      <p className="filter">
        {id ? 'Geteilte Notiz' : 'Von Freunden mit dir geteilt'}
        <a href={id ? '#geteilt' : '#tagebuch'} aria-label="Zurück">
          ✕
        </a>
      </p>
      {fehler && <p className="hinweis fehler">{fehler}</p>}
      {notizen?.length === 0 && (
        <div className="platzhalter">
          <p>Noch hat niemand etwas mit dir geteilt.</p>
          <p className="leise">
            Befreundete Benutzer können einzelne Notizen mit dir teilen. Freunde findest du unter <a href="#freunde">Konto → Freunde</a>.
          </p>
        </div>
      )}
      {notizen?.map((n) => <GeteilteKarte key={n.id} notiz={n} onGeaendert={ersetzen} onAusgeblendet={() => entfernen(n.id)} />)}
    </div>
  )
}

function GeteilteKarte({
  notiz,
  onGeaendert,
  onAusgeblendet,
}: {
  notiz: GeteilteNotiz
  onGeaendert: (n: GeteilteNotiz) => void
  onAusgeblendet: () => void
}) {
  const [anhoeren, setAnhoeren] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const aktion = async (f: () => Promise<void>) => {
    setFehler(null)
    setLaeuft(true)
    try {
      await f()
    } catch (e) {
      setFehler((e as Error).message)
    } finally {
      setLaeuft(false)
    }
  }

  const tag = tagesTitel(tagesDatum(new Date(notiz.aufgenommenAm), notiz.zeitzone), tagesDatum(new Date(), eigeneZeitzone()))

  return (
    <article className="karte notiz">
      <header>
        <strong className="von">{notiz.von}</strong>
        <time dateTime={notiz.aufgenommenAm}>
          {tag}, {uhrzeit(notiz)}
        </time>
        {notiz.dauerMs != null && <span className="leise">{dauer(notiz.dauerMs)}</span>}
        {!notiz.gesehen && <span className="chip neu">neu</span>}
      </header>
      {notiz.transkript && <p className="transkript">{notiz.transkript}</p>}
      {notiz.breite != null && notiz.laenge != null && (
        <p className="leise klein">
          📍 {notiz.breite.toFixed(4)}, {notiz.laenge.toFixed(4)}
        </p>
      )}
      {fehler && <p className="fehler">{fehler}</p>}
      {anhoeren && <audio controls autoPlay preload="none" src={api.geteiltAudioUrl(notiz.id)} />}
      <footer>
        {notiz.hatAudio && (
          <button className="link" onClick={() => setAnhoeren(!anhoeren)}>
            {anhoeren ? 'Player schließen' : 'Anhören'}
          </button>
        )}
        {notiz.uebernommenAls ? (
          <a className="link-knopf" href={link.notiz(notiz.uebernommenAls)}>
            ✓ In deiner Kartei
          </a>
        ) : (
          <button
            className="link"
            disabled={laeuft}
            title="Legt eine eigene Kopie in deinem Tagebuch an; Personen und Termine daraus kommen in deine Kartei"
            onClick={() =>
              aktion(async () => {
                const kopie = await api.geteiltUebernehmen(notiz.id)
                onGeaendert({ ...notiz, uebernommenAls: kopie.id })
              })
            }
          >
            In meine Kartei übernehmen
          </button>
        )}
        <button
          className="link gefahr"
          disabled={laeuft}
          onClick={() =>
            confirm(`Diese Notiz von ${notiz.von} ausblenden? Eine übernommene Kopie bleibt erhalten.`) &&
            aktion(async () => {
              await api.geteiltAusblenden(notiz.id)
              onAusgeblendet()
            })
          }
        >
          Ausblenden
        </button>
      </footer>
    </article>
  )
}
